import React, { useRef } from 'react';
import '../Accounting/Accounting.css';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { QRCodeCanvas } from 'qrcode.react';

const SalesReceiptModal = ({ show, onClose, transaction }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();

  const formatNumber = (number, locale) => {
    // Ensure the number is a properly parsed float
    const parsedNumber = parseFloat(number);
    if (isNaN(parsedNumber)) {
      return '0.00';
    }
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parsedNumber);
  };

  const formatDate = (dateString, locale) => {
    const date = new Date(dateString);
    return moment(date).format('DD MMM YYYY');
  };

  // Payment method transformation function
  const transformPaymentMethod = (paymentMethod) => {
    const paymentMethods = {
      'bank_transfer': 'Bank Transfer',
      'credit_card': 'Credit Card',
      'cash': 'Cash',
      'debit_card': 'Debit Card',
      'online_payment': 'Online Payment',
    };
    
    return paymentMethods[paymentMethod] || paymentMethod || 'N/A';
  };

  // Generate QR code data from receipt number
  const generateQRCodeData = (receiptNo) => {
    if (!receiptNo) return 'RECEIPT-000000';
    return receiptNo;
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  if (!show || !transaction) {
    return null;
  }

  // Generate a receipt number if not provided
  const receiptNo = transaction.document_no || `RE-${moment().format('YYYYMMDDHHmm')}`;
  
  // Generate QR code data
  const qrCodeData = generateQRCodeData(receiptNo);

  return (
    <div className="invoice-modal-overlay" style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="invoice-modal" ref={componentRef} style={{ 
        backgroundColor: 'white',
        padding: '16px',
        width: '210mm',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative',
      }}>
        <span className="modal-close-button" onClick={onClose} style={{
          position: 'absolute',
          top: '8px',
          right: '12px',
          fontSize: '20px',
          cursor: 'pointer',
          zIndex: 1,
          color: '#333',
          fontWeight: 'bold'
        }}>&times;</span>
        
        {/* Header section with logo and company info - Compact */}
        <div className="modal-header" style={{ 
          display: 'flex', 
          marginBottom: '12px',
          alignItems: 'flex-start'
        }}>
          {/* Left side - Logo */}
          <div className="company-logo" style={{ 
            width: '70px', 
            height: '70px',
            marginRight: '16px'
          }}>
            <img src="/images/keaes150.png" alt="KEAES" style={{ 
              width: '120%',
              height: 'auto'
            }} />
          </div>
          
          {/* Middle - Company info */}
          <div style={{ flex: 1, paddingTop: '2px' }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              marginBottom: '4px',
              marginTop: 0,
              color: '#333',
              textAlign: 'left',
              marginLeft: '24px'
            }}>Keaes Academics (Ekamai)</h3>
            <p style={{ 
              fontSize: '10px', 
              margin: '1px 0',
              lineHeight: '1.2',
              color: '#4a4a4a',
              textAlign: 'left',
              marginLeft: '24px'
            }}>982/22 Gateway Ekamai, 4th Fl. Sukhumvit Rd,</p>
            <p style={{ 
              fontSize: '10px', 
              margin: '1px 0',
              lineHeight: '1.2',
              color: '#4a4a4a',
              textAlign: 'left',
              marginLeft: '24px'
            }}>Phra Khanong, Khlong Toei, Bangkok 10110</p>

            <p style={{ 
              fontSize: '10px', 
              margin: '1px 0',
              lineHeight: '1.2',
              color: '#4a4a4a',
              textAlign: 'left',
              marginLeft: '24px'
            }}>Tel: 02-108-2922</p>
            <p style={{ 
              fontSize: '10px', 
              margin: '1px 0',
              lineHeight: '1.2',
              color: '#4a4a4a',
              textAlign: 'left',
              marginLeft: '24px'
            }}>Email: contact@keaes.com</p>
          </div>
          
          {/* Right side - Receipt box */}
          <div style={{ 
            background: 'rgba(200, 200, 200, 0.3)', 
            padding: '12px', 
            color: '#333',
            borderRadius: '6px',
            width: '180px',
            textAlign: 'center',
            marginLeft: 'auto',
            maxWidth: 180
          }}>
            <h2 style={{ 
              color: '#333', 
              fontSize: '16px', 
              margin: '0 0 4px 0',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {t('receipt').toUpperCase()}
            </h2>
            <div className="invoice-details" style={{ marginBottom: 4 }}>
              <div className="detail-row" style={{ marginBottom: 2 }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontSize: '10px', 
                  margin: '2px 0' 
                }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>{t('No.')}:</span>
                  <span style={{ color: '#333', marginLeft: 6 }}>{receiptNo}</span>
                </div>
              </div>
              <div className="detail-row" style={{ marginBottom: 2 }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontSize: '10px', 
                  margin: '2px 0' 
                }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>{t('date')}:</span>
                  <span style={{ color: '#333', marginLeft: 6 }}>{formatDate(transaction.payment_date || transaction.transaction_date, i18n.language)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Customer Information - Compact */}
        <div className="customer-info" style={{ marginBottom: '12px' }}>
          <h3 style={{ 
            fontSize: '13px', 
            fontWeight: 'bold', 
            marginBottom: '8px',
            color: '#333',
          }}>{t('Customer Information')}</h3>
          <div style={{
            backgroundColor: '#f9f9f9',
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '8px 12px'
          }}>
            <p style={{ fontSize: '11px', margin: '3px 0', color: '#333' }}>
              <strong>{t('name')}:</strong> {transaction.student_firstname} {transaction.student_lastname}
            </p>
            <p style={{ fontSize: '11px', margin: '3px 0', color: '#333' }}>
              <strong>{t('address')}:</strong> {transaction.address || t('na')}
            </p>
            <p style={{ fontSize: '11px', margin: '3px 0', color: '#333' }}>
              <strong>{t('phone')}:</strong> {transaction.phone || t('na')}
            </p>
            <p style={{ fontSize: '11px', margin: '3px 0', color: '#333' }}>
              <strong>{t('email')}:</strong> {transaction.email || t('na')}
            </p>
          </div>
        </div>
        
        <hr style={{ 
          border: 'none', 
          height: '1px', 
          backgroundColor: '#ccc', 
          margin: '12px 0'
        }} />
        
        {/* Receipt Table */}
        <div className="invoice-table" style={{ marginTop: '12px' }}>
          <div style={{ 
            width: '100%', 
            fontSize: '11px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            {/* Header Row */}
            <div style={{
              background: 'rgba(200, 200, 200, 0.3)', 
              padding: '4px 8px',
              borderBottom: '1px solid #495057',
              fontSize: 11,
              fontWeight: 'bold',
              color: '#333',
              borderRadius: '4px 4px 0 0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '5%', textAlign: 'center' }}>#</div>
                <div style={{ width: '45%', paddingLeft: '4px' }}>{t('description')}</div>
                <div style={{ width: '10%', textAlign: 'center' }}>{t('quantity')}</div>
                <div style={{ width: '10%', textAlign: 'center' }}>{t('unit')}</div>
                <div style={{ width: '15%', textAlign: 'right' }}>{t('unitPrice')}</div>
                <div style={{ width: '15%', textAlign: 'right' }}>{t('total')}</div>
              </div>
            </div>

            {/* Table Body as Card Rows */}
            <div>
              {transaction.items && transaction.items.map((item, index) => (
                <div 
                  key={index} 
                  style={{ 
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                    padding: '4px 8px',
                    borderBottom: index === transaction.items.length - 1 ? 'none' : '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '28px'
                  }}
                >
                  <div style={{ width: '5%', textAlign: 'center', color: '#333', fontWeight: 'bold', fontSize: '11px' }}>{index + 1}</div>
                  <div style={{ width: '45%', paddingLeft: '4px', color: '#333', fontWeight: '500', fontSize: '11px' }}>{item.item_name}</div>
                  <div style={{ width: '10%', textAlign: 'center', color: '#333', fontSize: '11px' }}>{item.quantity}</div>
                  <div style={{ width: '10%', textAlign: 'center', color: '#333', fontSize: '11px' }}>Item</div>
                  <div style={{ width: '15%', textAlign: 'right', color: '#333', fontSize: '11px' }}>฿{formatNumber(item.price_per_unit, i18n.language)}</div>
                  <div style={{ width: '15%', textAlign: 'right', color: '#333', fontWeight: 'bold', fontSize: '11px' }}>฿{formatNumber(item.price_per_unit * item.quantity, i18n.language)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Summary Information - Compact */}
        <div className="invoice-summary" style={{ 
          fontSize: 11,
          border: '1px solid #6c757d',
          padding: 8,
          background: 'linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%)',
          borderRadius: 6,
          width: 280,
          marginLeft: 'auto',
          marginTop: '12px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', color: '#333' }}>
            <span>{t('total')}:</span>
            <span>฿{formatNumber(transaction.total_amount, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
            <span style={{ color: '#333' }}>{t('discount')}:</span>
            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>-฿{formatNumber(transaction.discount || 0, i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', color: '#333' }}>
            <span>{t('Total After Discount')}:</span>
            <span>฿{formatNumber((transaction.total_amount || 0) - (transaction.discount || 0), i18n.language)}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', color: '#333' }}>
            <span>{t('vat', { percentage: 7 })}:</span>
            <span>฿{formatNumber(transaction.vat || 0, i18n.language)}</span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            margin: '6px 0 4px 0',
            fontWeight: 'bold',
            fontSize: '12px',
            borderTop: '1px solid #6c757d',
            paddingTop: '4px',
            color: '#333'
          }}>
            <span>{t('grandTotal')}:</span>
            <span>฿{formatNumber(transaction.final_amount, i18n.language)}</span>
          </div>
        </div>
        
        {/* Payment Information - Compact */}
        <div className="payment-info" style={{ 
          marginTop: '12px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{ 
            fontSize: '10px',
            textAlign: 'center',
            maxWidth: '400px',
            backgroundColor: '#f9f9f9',
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '8px 12px'
          }}>
            <div style={{ 
              fontWeight: 'bold', 
              marginBottom: '6px', 
              color: '#333', 
              fontSize: '11px',
              borderBottom: '1px solid #ccc',
              paddingBottom: '3px'
            }}>
              {t('Payment & Transaction Information')}
            </div>
            
            <div style={{ lineHeight: '1.4', color: '#333' }}>
              <div><strong>{t('Method')}:</strong> {transformPaymentMethod(transaction.payment_method)}</div>
              
              {transaction.payment_method === 'bank_transfer' && (transaction.bank_name || transaction.account_number) && (
                <div style={{ margin: '2px 0' }}>
                  <strong>{t('Bank')}:</strong> {transaction.bank_name} | 
                  <strong> {t('Account')}:</strong> {transaction.account_number}
                  {transaction.account_name && <span> | <strong>{t('Name')}:</strong> {transaction.account_name}</span>}
                  {transaction.branch && <span> | <strong>{t('Branch')}:</strong> {transaction.branch}</span>}
                </div>
              )}
              
              <div><strong>{t('Payment Status')}:</strong> <span style={{ color: '#155724', fontWeight: 'bold' }}>{t('Confirmed')}</span></div>
            </div>
          </div>
        </div>

        {/* QR Code, Signature, and Print Info Footer - Compact */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-end',
          marginTop: '16px',
          marginBottom: '12px',
          padding: '12px 0',
          borderTop: '1px solid #ccc'
        }}>
          {/* QR Code Section */}
          <div style={{ 
            width: '25%', 
            textAlign: 'center'
          }}>
            <QRCodeCanvas
              value={qrCodeData}
              size={50}
              level="M"
            />
            <div style={{ 
              fontSize: '9px', 
              marginTop: '4px', 
              color: '#495057',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              fontWeight: 'bold'
            }}>
              RECEIPT
            </div>
          </div>
          
          {/* Signature Section */}
          <div style={{ 
            width: '50%', 
            textAlign: 'center',
            padding: '0 16px'
          }}>
            <div>
              <div style={{ 
                fontSize: '11px', 
                fontWeight: 'bold', 
                marginBottom: '60px',
                color: '#333'
              }}>
                Authorized by
              </div>
              <div style={{ 
                borderTop: '1px solid #6c757d', 
                width: '240px', 
                margin: '0 auto',
                paddingTop: '6px',
                fontSize: '11px',
                color: '#333'
              }}>
                {transaction.created_by_firstname && transaction.created_by_lastname ? (
                  <div style={{ fontWeight: '500' }}>
                    {transaction.created_by_firstname} {transaction.created_by_lastname}
                  </div>
                ) : (
                  <div style={{ color: '#495057' }}>
                    Authorized Staff
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Print Info Section */}
          <div style={{ 
            width: '25%', 
            textAlign: 'right', 
            fontSize: '10px', 
            color: '#495057'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>
              Printed On:
            </div>
            <div style={{ marginBottom: '1px' }}>
              {moment().format('DD MMM YYYY')}
            </div>
            <div>
              {moment().format('HH:mm:ss')}
            </div>
          </div>
        </div>
        
        {/* Note - Compact */}
        <div className="invoice-note" style={{ 
          textAlign: 'center', 
          marginTop: '8px',
          marginBottom: '12px'
        }}>
          <p style={{ fontSize: '9px', color: '#495057', marginBottom: '4px' }}>
            {t('Note: We appreciate your business. Please note that all transactions are final, and refunds cannot be processed.')}
          </p>
        </div>
        
        {/* Print Button - Compact */}
        <div className="invoice-actions" 
            style={{ 
              display: 'flex', 
              justifyContent: 'center',
              marginTop: '12px',
              '@media print': {
                display: 'none'
              }
            }}>
          <button 
            onClick={handlePrint}
            style={{
              padding: '8px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: '1px solid #495057',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#495057';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#6c757d';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {t('printReceipt')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesReceiptModal;